import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { useSelector } from 'react-redux'
import { v4 } from "uuid"
import { getCurrentBuild } from 'web/data/core-data'
import { FightSetup, SetupFightEvent } from "web/data/models"
import { expectOneById, GroupedList } from 'web/utils/utils'
import { RootState } from '../store'

/*
    TODO: Should this be renamed FightSetups or something instead? I keep using
    the term "setup" everywhere, not "assignment".
*/

export type SetupState = {
    setups: FightSetup[]
}

const initialState: SetupState = {
    setups: []
}

const slice = createSlice({
    name: "setups",
    initialState,
    reducers: {

        cleanupDeprecated_fromRosterPlayerId_field: (state) => {
            state.setups.forEach(setup => {

                setup.fightPlayers = setup.fightPlayers.map(player => {
                    const needsCleanup = (
                        'fromRosterPlayerId' in player ||
                        'dep_fromRosterPlayerId' in player
                    )

                    if (needsCleanup) {
                        console.log(`Running data cleanup on player ${player.name}, ${player.id}`)

                        return {
                            id: player.id,
                            name: player.name,
                            patchInfo: player.patchInfo,
                            specId: player.specId,
                            modifierIds: player.modifierIds,
                            talentIds: player.talentIds,
                            overrides: player.overrides,
                        }
                    }

                    return player
                })
            })

            return state
        },

        createNewSetup: (state, action: PayloadAction<{ fightId: string; id?: string }>) => {
            state.setups.push({
                fightId: action.payload.fightId,
                id: action.payload.id ?? v4(),
                name: "Untitled setup",
                fightPlayers: [],
                spellAssignments: [],
                teamAssignments: [],
                setupFightEvents: [],

                patchInfo: getCurrentBuild().patchInfo,

                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            })
        },

        addNewSetup: (state, action: PayloadAction<{ setup: FightSetup }>) => {
            state.setups.push(action.payload.setup)
        },

        deleteSetup: (state, action: PayloadAction<{ setupId: string }>) => {
            state.setups = state.setups.filter(s => s.id !== action.payload.setupId)
        },

        deleteSetupsForFight: (state, action: PayloadAction<{ fightId: string }>) => {
            state.setups = state.setups.filter(s => s.fightId !== action.payload.fightId)
        },

        updateSetup: (state, action: PayloadAction<{ setup: FightSetup }>) => {
            const { setup } = action.payload

            state.setups = [
                ...state.setups.filter(s => s.id !== setup.id),
                {
                    ...setup,
                    updatedAt: new Date().toISOString()
                },
            ]
        },

        /**
         * Removes any orphaned assignments and prunes any setup fight
         * events that were lost due to parent segments getting
         * deleted.
         */
        removeOrphanAssignmentsAndUpdateSetupFightEvents: (state, action: PayloadAction<{
            fightId: string
            eventIds: {
                fromFight: string[]
                fromFightSetups: string[]
            }
            eventSetupData?: {
                eventsPerSetup: GroupedList<SetupFightEvent>
                setupIds: Set<string>
            }
        }>) => {

            // Prune assignments from missing events.
            const { fightId, eventIds } = action.payload

            const mergedEventIds = [
                ...eventIds.fromFight,
                ...eventIds.fromFightSetups,
            ]

            const affectedSetups = state.setups.filter(s => s.fightId === fightId)

            affectedSetups.forEach(setup => {
                setup.spellAssignments = setup.spellAssignments
                    .filter(sa => {
                        return mergedEventIds.findIndex(e => e === sa.fightEventId) >= 0
                    })

                setup.teamAssignments = setup.teamAssignments
                    .filter(sa => mergedEventIds.findIndex(e => e === sa.fightEventId) >= 0)
            })

            // Sometimes we will also have to update the setup events.
            if (action.payload.eventSetupData) {
                const { eventsPerSetup, setupIds } = action.payload.eventSetupData

                setupIds.forEach(setupId => {
                    const setup = expectOneById(state.setups, setupId)
                    setup.setupFightEvents = eventsPerSetup.get(setupId)
                })
            }

        },

    },

})

function selectors(state: SetupState) {

    return {

        getSetupsForFight: (fightId: string) => {
            return state.setups.filter(s => s.fightId === fightId)
        },

    }

}

export const SetupActions = slice.actions
export const SetupReducer = slice.reducer

export const useSetupState = () => useSelector<RootState, SetupState>(state => state.setup)

export const useSetupSelectors = () => {
    const state = useSelector<RootState, SetupState>(state => state.setup)
    return selectors(state)
}
